import React, { useEffect, useState, useRef } from 'react'
import { SpeechSynthesisUtterance, SpeechSynthesisPonyfillType } from '@davi-ai/web-speech-cognitive-services-davi'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { useTelemetry } from '../Contexts/TelemetryContext'

import type { CreateUtteranceParams } from '../../models/speechTypes'
import type { RetorikActivity } from '../../models/activityTypes'
import { Mode, RecognitionState, Routes, TraceType } from '../../models/enums'
import { selectVoice } from '../../utils/selectVoice'
import { checkSayAsWordsAndCreateUtterance } from '../../utils/speechSynthesisUtils'

import RetorikSpeech from './RetorikSpeech'

interface SynthesisManagerProps {
  ponyfill: SpeechSynthesisPonyfillType
  isRetorikNews?: boolean
}

const SynthesisManager = ({ ponyfill, isRetorikNews }: SynthesisManagerProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const cancel = useSelector((state: RootState) => state.speechReducer.cancel)
  const streamingQueue = useSelector((state: RootState) => state.speechReducer.streamingQueue)
  const voice = useSelector((state: RootState) => state.speechReducer.voice)
  const customVoice = useSelector((state: RootState) => state.speechReducer.customVoice)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const externalTextToSpeech = useSelector((state: RootState) => state.speechReducer.externalTextToSpeech)
  const isMicrophoneAllowed = useSelector((state: RootState) => state.speechReducer.isMicrophoneAllowed)
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const replacementLocales = useSelector((state: RootState) => state.localeReducer.replacementLocales)
  const agentData = useSelector((state: RootState) => state.retorikReducer.agentData)
  const appAvailable = useSelector((state: RootState) => state.retorikReducer.appAvailable)
  const preventExpectedInputHint = useSelector((state: RootState) => state.retorikReducer.configuration.preventExpectedInputHint)
  const speechSynthesisOptions = useSelector((state: RootState) => state.retorikReducer.configuration.speechSynthesisOptions)
  const sendTrace = useTelemetry().sendTrace

  const [utterance, setUtterance] = useState<SpeechSynthesisUtterance | null>(null)
  const [nextUtterance, setNextUtterance] = useState<SpeechSynthesisUtterance | null>(null)
  const [forceSpeechCaching, setForceSpeechCaching] = useState<boolean>(false)

  const isNewsViewRef = useRef<boolean>(!!isRetorikNews)
  const lastListTextPlayedRef = useRef<string>('')
  const streamingQueueRef = useRef<Array<RetorikActivity>>([])
  const currentActivityRef = useRef<RetorikActivity | null>(null)

  /**
   * Check activity locale in case of voice change during the speech, to keep the language defined in activity
   * instead of switching languages between answers that are written in the same language
   * @param {string} activityLocale
   * @returns {SpeechSynthesisVoice | null}
   */
  const getVoiceAfterCheckingActivityLocale = (activityLocale: string): SpeechSynthesisVoice | null => {
    return activityLocale === locale ? voice : selectVoice(ponyfill.speechSynthesis.getVoices(), activityLocale, customVoice, agentData)
  }

  /**
   * Check if the activity is a list one, to prevent text re-reading on state update
   * @param {CreateUtteranceParams} params
   * @returns {SpeechSynthesisUtterance}
   */
  const checkActivityAndCreateUtterance = (params: CreateUtteranceParams): SpeechSynthesisUtterance => {
    if (params.activity.attachmentLayout && params.activity.attachmentLayout.toLowerCase() === 'davilist') {
      const textToSpeak = params.activity.speak || params.activity.text
      if (textToSpeak) {
        if (textToSpeak === lastListTextPlayedRef.current) {
          return checkSayAsWordsAndCreateUtterance({ ...params, voice: null }, speechSynthesisOptions?.sayAs)
        } else {
          lastListTextPlayedRef.current = textToSpeak
        }
      }
    }

    return checkSayAsWordsAndCreateUtterance(params, speechSynthesisOptions?.sayAs)
  }

  const resetData = (): void => {
    // Set all activities currently in the queue as ended
    streamingQueueRef.current?.forEach((activity) => {
      dispatch(storeActions.activity.addEndedActivity(activity.id))
    })
    streamingQueueRef.current = []
    dispatch(storeActions.speech.killCurrentStreaming())

    currentActivityRef.current = null
    setUtterance(null)
    setNextUtterance(null)
    dispatch(storeActions.speech.resetCancelCurrentUtterance())
    dispatch(storeActions.speech.setSpeaking(false))
    dispatch(storeActions.speech.setBoundaryData([]))
  }

  const checkReplacementLocaleAndGetUtterance = (activity: RetorikActivity, checkReplacement: boolean = true): SpeechSynthesisUtterance => {
    const replacementLocale = checkReplacement ? replacementLocales?.[locale] || locale : locale
    const params: CreateUtteranceParams = {
      activity: activity,
      voice: getVoiceAfterCheckingActivityLocale(replacementLocale),
      locale: replacementLocale
    }

    return checkActivityAndCreateUtterance(params)
  }

  /**
   * Check if the microphone can be opened automatically
   */
  const checkDictateState = (): void => {
    if (!preventExpectedInputHint && mode === Mode.vocal && activeRecognitionState === RecognitionState.Closed) {
      if (!isMicrophoneAllowed) {
        dispatch(storeActions.speech.askPermission())
      } else {
        dispatch(storeActions.speech.toggleMicrophone(true))
      }
    }
  }

  /**
   * On route change, reset all data and check if we are on the news view
   */
  useEffect(() => {
    resetData()
    if (!isRetorikNews) isNewsViewRef.current = route === Routes.News || route === Routes.NewsInVeille
    // Force speech caching when using Retorik News or when using news in veille
    setForceSpeechCaching(isRetorikNews || route === Routes.NewsInVeille)
  }, [route])

  /**
   * On cancel call, reset all data and kill current streaming
   */
  useEffect(() => {
    cancel && resetData()
  }, [cancel])

  /**
   * Play external speech immediately but don't add it to the history
   */
  useEffect(() => {
    if (externalTextToSpeech) {
      const activity = {
        speak: externalTextToSpeech,
        locale: locale
      } as RetorikActivity
      currentActivityRef.current = activity
      setUtterance(checkReplacementLocaleAndGetUtterance(activity, false))
    }
  }, [externalTextToSpeech])

  const dispatchSpeechEvent = (start: boolean): void => {
    const speechEvent = start ? new Event('retorikSpeakStart') : new Event('retorikSpeakStop')
    document.dispatchEvent(speechEvent)
  }

  /**
   * Called when utterance's "start" event is fired
   */
  const handleUtteranceStart = (): void => {
    sendTrace(TraceType.utteranceStart, {
      activityId: currentActivityRef.current?.id,
      text: currentActivityRef.current?.speak || currentActivityRef.current?.text
    })
    dispatch(storeActions.speech.setSpeaking(true))

    if (!isNewsViewRef.current && currentActivityRef?.current) {
      dispatch(storeActions.speech.setCurrentOrLastPlayedActivity(currentActivityRef.current))
      currentActivityRef.current.id && dispatch(storeActions.directline.dispatchMarkActivity({ id: currentActivityRef.current.id, speaking: true }))
    }
    dispatchSpeechEvent(true)

    // Set next utterance to prepare synthesis data in order to improve fluidity
    if (streamingQueueRef.current.length && !streamingQueueRef.current[0].value?.streamingCount) {
      const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[0], false)
      setNextUtterance((current) => (utt.text === current?.text ? null : utt))
    } else {
      setNextUtterance(null)
    }
  }

  /**
   * Called when utterance's "end" event is fired
   */
  const handleUtteranceEnd = (): void => {
    sendTrace(TraceType.utteranceEnd, {
      activityId: currentActivityRef.current?.id
    })
    dispatch(storeActions.speech.setSpeaking(false))
    dispatchSpeechEvent(false)

    if (isNewsViewRef.current) {
      dispatch(storeActions.utils.setRetorikNewsEnded(true))
      currentActivityRef.current = null
    } else {
      handleEnded()
    }
  }

  const handleEnded = (): void => {
    // Add played activity id to context's endedActivities
    if (currentActivityRef.current?.id) {
      dispatch(storeActions.activity.addEndedActivity(currentActivityRef.current.id))
      dispatch(storeActions.directline.dispatchMarkActivity({ id: currentActivityRef.current.id, speaking: false }))
    }

    if (streamingQueueRef.current?.length) {
      // Check if this is the message sent after streaming activities
      if (streamingQueueRef.current[0].value?.streamingCount) {
        dispatch(storeActions.activity.addEndedActivity(streamingQueueRef.current[0].id))
        // If there is another activity after this message, play it. If not, let's check if the microphone should be opened
        if (streamingQueueRef.current?.length > 1) {
          const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[1])
          currentActivityRef.current = streamingQueueRef.current[1]
          setUtterance(utt)
          dispatch(storeActions.speech.removeFirstFromStreamingQueue(true))
        } else {
          streamingQueueRef.current[0].inputHint === 'expectingInput' && checkDictateState()
          currentActivityRef.current = null
          setUtterance(null)
          setNextUtterance(null)
          dispatch(storeActions.speech.removeFirstFromStreamingQueue(false))
        }
      } else {
        const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[0])
        currentActivityRef.current = streamingQueueRef.current[0]
        setUtterance(utt)
        dispatch(storeActions.speech.removeFirstFromStreamingQueue(false))
      }
    } else {
      currentActivityRef.current?.inputHint === 'expectingInput' && checkDictateState()
      currentActivityRef.current = null
      setUtterance(null)
      setNextUtterance(null)
    }
  }

  /**
   * Wait for app loading and voice selection to begin playing speech
   */
  useEffect(() => {
    // Set ref from the state to use in events
    streamingQueueRef.current = streamingQueue

    if (appAvailable && voice && streamingQueue.length && !currentActivityRef.current) {
      handleEnded()
    }
  }, [appAvailable, voice, streamingQueue])

  return (
    <RetorikSpeech
      ponyfill={ponyfill}
      appAvailable={appAvailable}
      utterance={utterance}
      nextUtterance={nextUtterance}
      forceSpeechCaching={forceSpeechCaching}
      onStart={handleUtteranceStart}
      onError={handleUtteranceEnd}
      onEnd={handleUtteranceEnd}
    />
  )
}

export default SynthesisManager
